<template>
  <div class="personal-consumption-history">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h5 class="mb-0">
        <font-awesome-icon icon="chart-line" class="me-2" />
        Persönliche Quittung-Statistik
      </h5>
      <div class="text-muted small">
        <span v-if="activeTab === 'consumption'">
          {{ totalConsumed }} {{ totalConsumed === 1 ? 'Quittung' : 'Quittungen' }} verbraucht
        </span>
        <span v-else>
          {{ totalTransferred }} {{ totalTransferred === 1 ? 'Transfer' : 'Transfers' }}
        </span>
      </div>
    </div>

    <!-- Tab Navigation -->
    <ul class="nav nav-tabs mb-4" role="tablist">
      <li class="nav-item" role="presentation">
        <button
          class="nav-link"
          :class="{ active: activeTab === 'consumption' }"
          @click="switchTab('consumption')"
          type="button"
          role="tab"
        >
          <font-awesome-icon icon="receipt" class="me-2" />
          Verbrauch
        </button>
      </li>
      <li class="nav-item" role="presentation">
        <button
          class="nav-link"
          :class="{ active: activeTab === 'transfers' }"
          @click="switchTab('transfers')"
          type="button"
          role="tab"
        >
          <font-awesome-icon icon="exchange-alt" class="me-2" />
          Transfers
        </button>
      </li>
    </ul>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border spinner-border-sm" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-gradient-warning" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <!-- Consumption Tab Content -->
    <div v-else-if="activeTab === 'consumption'">
      <!-- Empty State -->
      <div v-if="consumptions.length === 0" class="text-center py-5 text-muted">
        <font-awesome-icon icon="receipt" size="3x" class="mb-3" />
        <h6>Noch keine Quittungen verbraucht</h6>
        <p class="mb-0">Du hast noch keine persönlichen Quittungen erstellt.</p>
      </div>

      <!-- Consumption History Table -->
      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead class="table-light">
            <tr>
              <th>Datum</th>
              <th>Beschreibung</th>
              <th>Quittung</th>
              <th class="text-end">Verbrauch</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="consumption in consumptions" :key="consumption.id">
              <td>
                <div class="fw-semibold">{{ formatDate(consumption.consumedAt) }}</div>
                <small class="text-muted">{{ formatTime(consumption.consumedAt) }}</small>
              </td>
              <td>
                <div class="fw-semibold">{{ consumption.description || 'Quittung verbraucht' }}</div>
                <small class="text-muted">{{ getConsumptionTypeLabel(consumption.consumptionType) }}</small>
              </td>
              <td>
                <div v-if="consumption.receipt">
                  <router-link 
                    :to="{ name: 'receipt-detail', params: { id: consumption.receiptId } }"
                    class="text-decoration-none d-flex align-items-center"
                  >
                    <font-awesome-icon icon="receipt" class="me-2 text-primary" />
                    <div>
                      <div class="fw-semibold">{{ consumption.receipt.title }}</div>
                      <small class="text-muted">{{ formatCurrency(consumption.receipt.amount) }}</small>
                    </div>
                    <font-awesome-icon icon="external-link-alt" class="ms-2 text-muted small" />
                  </router-link>
                </div>
                <div v-else class="text-muted">
                  <font-awesome-icon icon="trash" class="me-1" />
                  Quittung wurde gelöscht
                </div>
              </td>
              <td class="text-end">
                <span class="badge bg-danger">
                  -{{ consumption.consumedAmount }} {{ consumption.consumedAmount === 1 ? 'Quittung' : 'Quittungen' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Transfers Tab Content -->
    <div v-else-if="activeTab === 'transfers'">
      <!-- Empty State -->
      <div v-if="transfers.length === 0" class="text-center py-5 text-muted">
        <font-awesome-icon icon="exchange-alt" size="3x" class="mb-3" />
        <h6>Noch keine Transfers</h6>
        <p class="mb-0">Du hast noch keine Quittungen zwischen deinem persönlichen Account und Bereichen transferiert.</p>
      </div>

      <!-- Transfers History Table -->
      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead class="table-light">
            <tr>
              <th>Datum</th>
              <th>Bereich</th>
              <th>Typ</th>
              <th class="text-end">Betrag</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="transfer in transfers" :key="transfer.id">
              <td>
                <div class="fw-semibold">{{ formatDate(transfer.transferredAt) }}</div>
                <small class="text-muted">{{ formatTime(transfer.transferredAt) }}</small>
              </td>
              <td>
                <div class="fw-semibold">{{ transfer.team?.name || transfer?.teamName }}</div>
                <small class="text-muted">{{ transfer.team ? 'Bereich' : 'Bereich gelöscht' }}</small>
              </td>
              <td>
                <div class="fw-semibold">{{ getTransferTypeLabel(transfer.transferType) }}</div>
                <small class="text-muted">{{ getTransferDirectionLabel(transfer.transferType) }}</small>
              </td>
              <td class="text-end">
                <span :class="getTransferBadgeClass(transfer.transferType)">
                  {{ getTransferAmountLabel(transfer.transferType) }}{{ transfer.amount }} {{ transfer.amount === 1 ? 'Quittung' : 'Quittungen' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Pagination -->
    <nav v-if="pagination && pagination.totalPages > 1" class="mt-4">
      <div class="d-flex justify-content-center align-items-center gap-3">
        <button
          @click="previousPage"
          :disabled="pagination.page <= 1 || loading"
          class="btn btn-outline-secondary btn-sm"
        >
          ← Vorherige
        </button>
        
        <span class="text-muted small">
          Seite {{ pagination.page }} von {{ pagination.totalPages }}
        </span>
        
        <button
          @click="nextPage"
          :disabled="pagination.page >= pagination.totalPages || loading"
          class="btn btn-outline-secondary btn-sm"
        >
          Nächste →
        </button>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { coinTransferApi } from '@/api/coinTransfer'

const activeTab = ref('consumption')
const consumptions = ref([])
const transfers = ref([])
const loading = ref(false)
const error = ref(null)
const pagination = ref(null)

const totalConsumed = computed(() => {
  return consumptions.value.reduce((sum, consumption) => sum + consumption.consumedAmount, 0)
})

const totalTransferred = computed(() => {
  return transfers.value.length
})

const switchTab = (tab) => {
  activeTab.value = tab
  loadData()
}

const loadData = async (page = 1) => {
  loading.value = true
  error.value = null
  
  try {
    if (activeTab.value === 'consumption') {
      const result = await coinTransferApi.getPersonalConsumptionHistory(page, 10)
      consumptions.value = result.consumptions || []
      pagination.value = result.pagination
    } else {
      const result = await coinTransferApi.getPersonalTransferHistory(page, 10)
      transfers.value = result.transfers || []
      pagination.value = result.pagination
    }
  } catch (err) {
    console.error('Fehler beim Laden der Daten:', err)
    error.value = activeTab.value === 'consumption' 
      ? 'Verbrauchshistorie konnte nicht geladen werden'
      : 'Transfer-Historie konnte nicht geladen werden'
    consumptions.value = []
    transfers.value = []
    pagination.value = null
  } finally {
    loading.value = false
  }
}

const previousPage = () => {
  if (pagination.value.page > 1) {
    loadData(pagination.value.page - 1)
  }
}

const nextPage = () => {
  if (pagination.value.page < pagination.value.totalPages) {
    loadData(pagination.value.page + 1)
  }
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('de-DE')
}

const formatTime = (dateString) => {
  return new Date(dateString).toLocaleTimeString('de-DE', { 
    hour: '2-digit', 
    minute: '2-digit' 
  })
}

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount)
}

const getConsumptionTypeLabel = (type) => {
  const labels = {
    'receipt_generation': 'Quittungserstellung',
    'other': 'Sonstige'
  }
  return labels[type] || type
}

const getTransferTypeLabel = (type) => {
  const labels = {
    'personal_to_team': 'Personal → Bereich',
    'team_to_personal': 'Bereich → Personal'
  }
  return labels[type] || type
}

const getTransferDirectionLabel = (type) => {
  const labels = {
    'personal_to_team': 'Ausgehender Transfer',
    'team_to_personal': 'Eingehender Transfer'
  }
  return labels[type] || type
}

const getTransferAmountLabel = (type) => {
  return type === 'personal_to_team' ? '-' : '+'
}

const getTransferBadgeClass = (type) => {
  return type === 'personal_to_team' ? 'badge bg-danger' : 'badge bg-success'
}

watch(activeTab, () => {
  loadData()
})

onMounted(() => {
  loadData()
})
</script>
